import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions, retryOnTransient } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { getSchedules, getSchedule, getScheduleCredentials } from '../functions/schedulesApi';
import { getScheduleRuns, getScheduleRunSession } from '../functions/scheduleRunsApi';
import type {
  SchedulesListResponse,
  ScheduleDefinition,
  RunsListResponse,
  CredentialStatus,
} from 'src/types/Schedules';
import type { ScheduleRunSession } from 'src/chat/types';

export function useSchedulesQuery(
  status?: 'active' | 'paused' | 'all',
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<SchedulesListResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<SchedulesListResponse>(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getSchedules(accessToken, status)),
    [accessToken, status],
  );

  return useQuery<SchedulesListResponse, FetchError>(
    [queryNames.schedules, status],
    queryFn,
    // `retry: retryOnTransient` matters here beyond tidiness. This endpoint
    // answers a plan gate with a 403, and react-query's default is 3 retries
    // with backoff — so a gated customer sat on "Loading schedules…" for
    // several seconds before the upgrade banner appeared, on the exact screen
    // meant to convert them. A 403 is authoritative; only 429/5xx are worth
    // retrying.
    { retry: retryOnTransient, ...actualQueryOptions, enabled: !!accessToken },
  );
}

export function useScheduleQuery(
  id: string | undefined,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ScheduleDefinition, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<ScheduleDefinition>(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getSchedule(accessToken, id!)),
    [accessToken, id],
  );

  return useQuery<ScheduleDefinition, FetchError>(
    [queryNames.schedule, id],
    queryFn,
    { ...actualQueryOptions, enabled: !!accessToken && !!id },
  );
}

export function useScheduleRunsQuery(
  scheduleId: string | undefined,
  limit = 50,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<RunsListResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<RunsListResponse>(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getScheduleRuns(accessToken, scheduleId!, limit)),
    [accessToken, scheduleId, limit],
  );

  return useQuery<RunsListResponse, FetchError>(
    [queryNames.scheduleRuns, scheduleId],
    queryFn,
    { ...actualQueryOptions, enabled: !!accessToken && !!scheduleId },
  );
}

export function useScheduleRunSessionQuery(
  sessionId: string | null,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ScheduleRunSession, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<ScheduleRunSession>(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getScheduleRunSession(accessToken, sessionId!)),
    [accessToken, sessionId],
  );

  return useQuery<ScheduleRunSession, FetchError>(
    [queryNames.scheduleRunSession, sessionId],
    queryFn,
    { ...actualQueryOptions, enabled: !!accessToken && !!sessionId },
  );
}

export function useScheduleCredentialsQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<CredentialStatus, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<CredentialStatus>(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getScheduleCredentials(accessToken)),
    [accessToken],
  );

  return useQuery<CredentialStatus, FetchError>(
    [queryNames.scheduleCredentials],
    queryFn,
    { ...actualQueryOptions, enabled: !!accessToken },
  );
}
